import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { AlertTriangle, ChevronRight, Clock } from 'lucide-react';
import axios from 'axios';
import { API_CACHE_EVENT } from '../lib/apiCache';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const StaleProjectsBanner = () => {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    if (!user || user.role === 'guest' || user.role === 'duch') return;
    const fetch = async () => {
      try {
        const { data } = await axios.get(`${API_URL}/projects/stale`);
        setProjects(data);
      } catch {}
    };
    fetch();
    const interval = setInterval(fetch, 120000);
    window.addEventListener(API_CACHE_EVENT, fetch);
    return () => { clearInterval(interval); window.removeEventListener(API_CACHE_EVENT, fetch); };
  }, [user]);

  if (!projects.length) return null;

  const shown = projects.slice(0, 5);
  const remaining = projects.length - shown.length;

  return (
    <div className="mb-6 rounded-lg border-l-4 border-amber-500 p-4"
      style={{ backgroundColor: 'var(--crm-bg-card)', boxShadow: 'var(--crm-card-shadow)' }}
      data-testid="stale-projects-banner">
      <div className="flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-amber-500 mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-amber-600 dark:text-amber-300 font-semibold text-sm mb-2" data-testid="stale-banner-title">
            Masz {projects.length} {projects.length === 1 ? 'projekt wymagający' : 'projektów wymagających'} uwagi:
          </p>
          <div className="space-y-1.5">
            {shown.map(p => (
              <div key={p.id}
                data-testid={`stale-project-${p.id}`}
                className={`flex items-center gap-2 rounded-md px-3 py-2 text-sm ${
                  p.reminder_level >= 3
                    ? 'bg-red-50 dark:bg-red-500/15 border border-red-200 dark:border-red-500/30'
                    : 'bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/20'
                }`}>
                <Clock className={`w-3.5 h-3.5 shrink-0 ${p.reminder_level >= 3 ? 'text-red-500 dark:text-red-400' : 'text-amber-500 dark:text-amber-400'}`} />
                <span className="text-[var(--crm-text-primary)] truncate flex-1">
                  <span className="font-medium">{p.nazwa}</span>
                  <span className="text-[var(--crm-text-muted)] mx-1.5">—</span>
                  <span className="text-[var(--crm-text-secondary)]">{p.klient}</span>
                  <span className="text-[var(--crm-text-muted)] mx-1.5">—</span>
                  <span className="text-[var(--crm-text-muted)] text-xs">{p.status_label}</span>
                  <span className="text-[var(--crm-text-muted)] mx-1.5">—</span>
                  <span className={p.reminder_level >= 3 ? 'text-red-600 dark:text-red-400 font-semibold' : 'text-amber-600 dark:text-amber-400'}>
                    {p.inactive_days} dni
                  </span>
                  {user?.role === 'admin' && p.owner_initials && (
                    <span className="text-[var(--crm-text-muted)] ml-1.5 text-xs">({p.owner_initials})</span>
                  )}
                </span>
                <Link to={`/projects/${p.id}`}
                  data-testid={`stale-go-${p.id}`}
                  className="shrink-0 text-xs text-[#FF6B35] hover:text-[#e55a2a] font-medium flex items-center gap-0.5 transition-colors">
                  Przejdź <ChevronRight className="w-3 h-3" />
                </Link>
              </div>
            ))}
          </div>
          {remaining > 0 && (
            <p className="text-[var(--crm-text-muted)] text-xs mt-2">...i {remaining} więcej</p>
          )}
          <p className="text-[var(--crm-text-muted)] text-xs mt-2">
            Zaktualizuj status lub dodaj notatkę do historii kontaktów aby usunąć przypomnienie
          </p>
        </div>
      </div>
    </div>
  );
};
